import React, { useEffect, useState } from "react";
import { auth } from "./FirebaseConfig";
import { subscribeReconnect, flagReconnectNeeded } from "./connectionState";
import "./ConnectionWatcher.css";

// Global connectivity/auth watchdog. Shows a non-blocking banner when the browser
// goes offline, and — driven by the resilient Firestore listeners — when a session
// can't self-heal after a blip. On regaining connectivity it force-refreshes the
// auth token so Firestore stops rejecting requests; if that's not enough (dead
// listeners on a non-self-healing screen), it offers a one-tap reload.
export default function ConnectionWatcher() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [reconnectNeeded, setReconnectNeeded] = useState(false);

  useEffect(() => subscribeReconnect(setReconnectNeeded), []);

  useEffect(() => {
    const refreshToken = async () => {
      if (!auth.currentUser) return;
      try {
        await auth.currentUser.getIdToken(true);
        flagReconnectNeeded(false);
      } catch (_) {
        /* still unreachable; the banner stays until the next online event */
      }
    };
    const onOnline = () => { setOnline(true); refreshToken(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  if (online && !reconnectNeeded) return null;

  return (
    <div className={`conn-banner ${online ? "conn-warn" : "conn-offline"}`} role="status">
      {!online ? (
        <span>You're offline — we'll reconnect automatically when your connection returns.</span>
      ) : (
        <>
          <span>Connection hiccup — your session may need to reconnect.</span>
          <button className="conn-reconnect" onClick={() => window.location.reload()}>
            Reconnect
          </button>
        </>
      )}
    </div>
  );
}
